/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React from 'react';

import { cssMap, cx, jsx } from '@compiled/react';
import { di } from 'react-magnetic-di';

import AnalyticsContext from '@atlaskit/analytics-next/AnalyticsContext';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';
import { useNavigationStyle } from '@atlassian/company-hub-common/controllers/navigation-style-context';
import { setHubBrandingStyle } from '@atlassian/company-hub-common/controllers/set-hub-branding-style';
import { useUnsetGlobalTheme } from '@atlassian/company-hub-common/controllers/use-unset-global-theme';
import { HubBreadcrumbs } from '@atlassian/company-hub-common/hub-breadcrumbs';
import { HubBreadcrumbsProvider } from '@atlassian/company-hub-common/hub-breadcrumbs-context';
import { useHubTheming } from '@atlassian/company-hub-common/hub-theming-context';
import { HubTypographyScope } from '@atlassian/company-hub-common/hub-typography-scope';
import {
	HUB_FEATURES,
	useIsHubFeatureEnabled,
} from '@atlassian/company-hub-common/use-is-hub-feature-enabled';
import { useHubContext } from '@atlassian/company-hub-common/utils';
import { isDefaultHelpCenterBasePath } from '@atlassian/company-hub-common/utils/help-center-base-path';

import { Banner } from './banner';
import { MigrationPublishSuccessFlag } from './migration-publish-success-flag';
import { MigrationReviewActionBar } from './migration-review-action-bar';
import { Navbar } from './navbar';

const HELP_CENTER_PREVIEW_PATH_PREFIX = '/helpcenter/test/';
const HELP_CENTER_LIVE_PATH_PREFIX = '/helpcenter/';
const DEFAULT_HELP_CENTER_PORTALS_LIVE_PATH = '/servicedesk/customer/portals';
const MIGRATION_REVIEW_ACTION_BAR_ANALYTICS_SOURCE = 'helpCenterMigrationReviewActionBar';

// Existing global-link selectors require the unbounded Compiled API.
const containerStyles = cssMap({
	bannerContainer: {
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
		paddingBottom: token('space.300'),
	},
	container: {
		boxSizing: 'border-box',
		appearance: 'none',
		border: 'none',
		position: 'relative',
		backgroundColor: token('elevation.surface.raised'),
		minHeight: '100%',
		display: 'flex',
		flexDirection: 'column',
		// #ref - help-center/packages/common-utils/styles/src/global.tsx
		// we can't use imported tokens here so hard coding the value instead but this is from the @atlassian/company-hub-common package
		/* eslint-disable @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors */
		'& a:not(.cv-exclude-from-customisation):not([data-exclude-global-styling="true"])': {
			color: 'var(--hub-primary-link-color)',
		},
		'& a:hover:not(.cv-exclude-from-customisation):not([data-exclude-global-styling="true"])': {
			color: 'var(--hub-primary-link-color)',
		},
	},
	childrenWrapper: {
		paddingBottom: token('space.600'),
	},
	childrenWrapperEditMode: {
		// In edit mode, the wrapper must fill the remaining flex space so
		// that the editor's `height: 100%` chain resolves correctly.
		// Without this, the config panel loses its fixed height and scrolls
		// together with the page content instead of independently.
		flexGrow: 1,
		minHeight: '0px',
	},
});

// Compiled supports the inherited custom background, which ADS bounded styles do not.
const navigationStyles = cssMap({
	scrollableContainer: {
		overflow: 'auto',
	},
	// Cover the top gap, but keep bottom spacing outside the painted sticky area.
	fixedNavContainer: {
		position: 'sticky',
		top: '0px',
		zIndex: 200,
		backgroundColor: 'inherit',
		paddingTop: token('space.300'),
		marginBottom: token('space.300'),
	},
});

export const HubAppShell = ({
	children,
	isEditMode = false,
	hideNavbar = false,
}: {
	children: React.ReactNode;
	/**
	 * When `true` the built-in Navbar is not rendered.  Use this together with
	 * the `editor-plugin-hub-shell` plugin which injects its own visual Navbar
	 * inside the editor content area so both elements resize together when the
	 * editor config panel opens.
	 */
	hideNavbar?: boolean;
	isEditMode?: boolean;
}): JSX.Element => {
	di(useHubContext, fg);
	const { cloudId, cloudUrl, env, layout, containerStyling, headerless, hubName, migrationReview } =
		useHubContext();
	const { options: navStyle } = useNavigationStyle();
	const isStickyNavFixEnabled = fg('hubs_nav_fixed_top_sticky_fix');
	const isFixedAtTop = Boolean(navStyle?.fixedAtTop);
	const isHeaderless = headerless && fg('csm-support-site-headerless-query-param');
	const shouldRenderShellNavigation = !isHeaderless && !hideNavbar;
	// When the sticky fix is enabled, the shell stops owning the scroll so the
	// fixed navbar can stick to the viewport. When padded, a sticky wrapper keeps
	// the top gap in place and covers scrolling content behind it.
	// Scoped to view mode — edit mode injects its own
	// navbar via `editor-plugin-hub-shell` and relies on the shell keeping its
	// own scroll container for the editor height chain.
	const shouldUseViewportStickyNav =
		isStickyNavFixEnabled && isFixedAtTop && !isEditMode && shouldRenderShellNavigation;
	const shouldWrapPaddedFixedNav = shouldUseViewportStickyNav && Boolean(navStyle?.padding);
	// Whenever the shell still owns scrolling, a fixed navbar relies on the shell
	// being full height. Tied to `shouldUseViewportStickyNav` rather than the gate
	// so edit mode keeps its height chain once the gate is enabled.
	const fixedNavFullHeightStyling =
		!shouldUseViewportStickyNav && isFixedAtTop ? { height: '100%' } : {};

	const showBanner = layout?.showBanner;
	const isHelpHubMigrationEnabled = fg('enable-help-hub-migration');
	const shouldApplyContainerStyling = fg('enable-jsm-in-hubs');
	const [isBreadcrumbsEnabled] = useIsHubFeatureEnabled([HUB_FEATURES.BREADCRUMBS]);
	// Edit mode injects the navbar and breadcrumbs into the editor content area so they
	// resize with the ProseMirror canvas and stay below the editor toolbar.
	const shouldRenderShellBreadcrumbs = !isHeaderless && !isEditMode;
	const publishedHelpCenterPath = env.relativeBaseUrl.startsWith(HELP_CENTER_PREVIEW_PATH_PREFIX)
		? env.relativeBaseUrl.replace(HELP_CENTER_PREVIEW_PATH_PREFIX, HELP_CENTER_LIVE_PATH_PREFIX)
		: // Matches the default Help Center with or without the `/portals` segment,
			// since the backend reports its base path without one.
			isDefaultHelpCenterBasePath(env.relativeBaseUrl)
			? DEFAULT_HELP_CENTER_PORTALS_LIVE_PATH
			: env.relativeBaseUrl;
	const publishedHelpCenterUrl = new URL(publishedHelpCenterPath, cloudUrl).toString();

	// Default hardcoded styles (used when feature flag is disabled for backward compatibility)
	const defaultContainerStyling = {
		borderRadius: token('radius.large'),
		boxShadow: token('elevation.shadow.overlay'),
	};

	// Apply containerStyling from props if feature flag is enabled, otherwise use hardcoded defaults
	const appliedContainerStyling = shouldApplyContainerStyling
		? containerStyling
		: defaultContainerStyling;

	// Built outside the JSX so the `enforce-style-prop` suppression covers the whole
	// object: these values are dynamic (theming props plus gated layout) and cannot
	// move into `cssMap`.
	const containerInlineStyles: React.CSSProperties = {
		...appliedContainerStyling,
		...fixedNavFullHeightStyling,
	};

	const hubContainerRef = React.useRef<HTMLDivElement>(null);
	useUnsetGlobalTheme(isEditMode ? hubContainerRef.current : null); // for view mode we don't need to unset the global theme

	// useHubTheming returns undefined outside <HubThemingProvider>, but
	// HubAppShell is always mounted inside HubBootstrap → provider is in tree.
	const { primaryColor, isAdvancedThemingEnabled } = useHubTheming()!;

	// Legacy theming path: when the v2 gate is OFF we restore the pre-v2
	// behaviour by writing the `--hub-primary-*` CSS vars into the document so
	// existing ADFs (which encode `var(--hub-primary-color, ...)` strings) and
	// chrome that consumes the same vars continue to look correct. When the
	// gate is ON the palette + adapter outputs from `useHubTheming()` drive
	// every consumer directly, so this effect is a no-op.
	React.useEffect(() => {
		if (isAdvancedThemingEnabled) {
			return undefined;
		}
		return setHubBrandingStyle({ primaryColor });
	}, [isAdvancedThemingEnabled, primaryColor]);

	return (
		<HubBreadcrumbsProvider>
			<HubTypographyScope>
				<FlagsProvider>
					{isHelpHubMigrationEnabled && <MigrationPublishSuccessFlag />}
					<Box
						xcss={cx(
							containerStyles.container,
							!shouldUseViewportStickyNav && navigationStyles.scrollableContainer,
						)}
						testId="hub-app-shell"
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Dynamic styles passed from outside via containerStyling prop
						style={containerInlineStyles}
						ref={hubContainerRef}
					>
						{!isEditMode && migrationReview && isHelpHubMigrationEnabled && (
							<AnalyticsContext data={{ source: MIGRATION_REVIEW_ACTION_BAR_ANALYTICS_SOURCE }}>
								<MigrationReviewActionBar
									cloudId={cloudId}
									helpCenterAri={env.helpCenterAri}
									helpCenterName={hubName}
									migrationReview={migrationReview}
									publishedHelpCenterUrl={publishedHelpCenterUrl}
								/>
							</AnalyticsContext>
						)}
						{shouldRenderShellNavigation &&
							(shouldWrapPaddedFixedNav ? (
								<div css={navigationStyles.fixedNavContainer} data-testid="hub-sticky-navbar">
									<Navbar isEditMode={isEditMode} isViewportSticky={shouldUseViewportStickyNav} />
								</div>
							) : (
								<Navbar isEditMode={isEditMode} isViewportSticky={shouldUseViewportStickyNav} />
							))}
						{isBreadcrumbsEnabled && shouldRenderShellBreadcrumbs && <HubBreadcrumbs />}
						{showBanner && (
							<div css={containerStyles.bannerContainer}>
								<Banner />
							</div>
						)}
						<Box
							xcss={
								isEditMode
									? containerStyles.childrenWrapperEditMode
									: containerStyles.childrenWrapper
							}
							testId="hub-app-shell-children-wrapper"
						>
							{children}
						</Box>
					</Box>
				</FlagsProvider>
			</HubTypographyScope>
		</HubBreadcrumbsProvider>
	);
};
